<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              :title="isDeletePage() != true ? 'Doctor Available Days Management' : 'Deleted Doctor Available Days'"
              :subtitle="isDeletePage() != true ? 'Manage doctor availability for each day of the week' : 'Restore or permanently delete doctor available days'"
            >
              <template v-slot:actions>
                <div v-if="isDeletePage() != true" class="d-flex justify-end align-center">
                    <v-btn
                      color="primary"
                      elevation="1"
                      small
                      to="/doctor-available-days/create"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add Available Day
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Available Days</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Days"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    placeholder="Monday, Tuesday..."
                    hide-details
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <!-- User Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.user_id"
                    :items="users"
                    label="User"
                    prepend-inner-icon="mdi-account"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Day Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.day"
                    :items="days"
                    label="Day"
                    prepend-inner-icon="mdi-calendar"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Availability Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.is_available"
                    :items="availabilityStatuses"
                    label="Availability"
                    prepend-inner-icon="mdi-check-circle"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Doctor Available Days Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-table</v-icon
                    >
                    <span class="text-body-1 font-weight-medium"
                      >Available Days List</span
                    >
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-calendar-check</v-icon>
                      {{ count }} {{ count === 1 ? "Day" : "Days" }}
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-2">
                      <span class="text-caption grey--text mr-1">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn x-small outlined :value="10">10</v-btn>
                        <v-btn x-small outlined :value="25">25</v-btn>
                        <v-btn x-small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>

                    <!-- Export Button -->
                    <v-btn
                      v-if="!isDeletePage()"
                      x-small
                      color="success"
                      outlined
                      @click="exportToExcel"
                      class="px-2"
                    >
                      <v-icon left x-small>mdi-microsoft-excel</v-icon>
                      Export
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <section
                v-if="isLoading"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <health-loader message="Loading available days..." />
                </div>
              </section>

              <!-- No Data State -->
              <section
                v-else-if="!isLoading && (!data || data.length === 0)"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <v-icon size="80" color="grey lighten-2"
                    >mdi-calendar-remove</v-icon
                  >
                  <p class="text-h6 grey--text mt-4 mb-2">No Available Days Found</p>
                  <p class="text-body-2 grey--text mb-4">
                    {{
                      hasActiveFilters
                        ? "Try adjusting your filters or clear them to see all available days."
                        : "Start by adding the first doctor available day."
                    }}
                  </p>
                  <v-btn
                    v-if="!hasActiveFilters && !isDeletePage()"
                    color="primary"
                    to="/doctor-available-days/create"
                  >
                    <v-icon left>mdi-plus</v-icon>
                    Add Available Day
                  </v-btn>
                  <v-btn
                    v-else
                    color="grey"
                    outlined
                    @click="clearAllFilters"
                  >
                    <v-icon left>mdi-filter-remove</v-icon>
                    Clear Filters
                  </v-btn>
                </div>
              </section>

              <!-- Data Table -->
              <v-simple-table v-else dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left font-weight-bold text-uppercase">
                        <v-icon small class="mr-1">mdi-account</v-icon>
                        User
                      </th>
                      <th class="text-left font-weight-bold text-uppercase">
                        <v-icon small class="mr-1">mdi-calendar</v-icon>
                        Day
                      </th>
                      <th class="text-center font-weight-bold text-uppercase">
                        <v-icon small class="mr-1">mdi-check-circle</v-icon>
                        Available
                      </th>
                      <th class="text-center font-weight-bold text-uppercase">
                        <v-icon small class="mr-1">mdi-information</v-icon>
                        Day Type
                      </th>
                      <th class="text-center font-weight-bold text-uppercase">
                        <v-icon small class="mr-1">mdi-toggle-switch</v-icon>
                        Status
                      </th>
                      <th class="text-center font-weight-bold text-uppercase">
                        <v-icon small class="mr-1">mdi-cog</v-icon>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(item, index) in data"
                      :key="index"
                      :class="{ 'grey lighten-5': index % 2 === 0 }"
                    >
                      <!-- User -->
                      <td class="py-3">
                        <div class="d-flex align-center">
                          <v-avatar size="32" class="mr-2">
                            <v-icon color="primary">mdi-account</v-icon>
                          </v-avatar>
                          <div>
                            <div class="text-body-2 font-weight-medium">
                              {{ item.user?.user_name || 'N/A' }}
                            </div>
                            <div class="text-caption grey--text">
                              {{ item.user?.email || 'N/A' }}
                            </div>
                          </div>
                        </div>
                      </td>

                      <!-- Day -->
                      <td class="py-3">
                        <v-chip
                          :color="getDayColor(item.day)"
                          outlined
                          small
                          class="font-weight-medium"
                        >
                          <v-icon left x-small>mdi-calendar</v-icon>
                          {{ item.day }}
                        </v-chip>
                      </td>

                      <!-- Available -->
                      <td class="text-center py-3">
                        <v-chip
                          :color="item.is_available ? 'success' : 'error'"
                          outlined
                          x-small
                        >
                          <v-icon left x-small>
                            {{ item.is_available ? 'mdi-check' : 'mdi-close' }}
                          </v-icon>
                          {{ item.is_available ? 'Available' : 'Not Available' }}
                        </v-chip>
                      </td>

                      <!-- Day Type -->
                      <td class="text-center py-3">
                        <v-chip
                          :color="getDayTypeColor(item.day)"
                          outlined
                          x-small
                        >
                          {{ getDayType(item.day) }}
                        </v-chip>
                      </td>

                      <!-- Status -->
                      <td class="text-center py-3">
                        <v-chip
                          :color="item.is_active ? 'success' : 'warning'"
                          outlined
                          x-small
                        >
                          <v-icon left x-small>
                            {{ item.is_active ? 'mdi-check-circle' : 'mdi-pause-circle' }}
                          </v-icon>
                          {{ item.is_active ? 'Active' : 'Inactive' }}
                        </v-chip>
                      </td>

                      <!-- Actions -->
                      <td class="text-center py-3">
                        <div class="d-flex justify-center align-center">
                          <!-- View Button -->
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                x-small
                                color="info"
                                :to="`/doctor-available-days/${item.id}`"
                                v-bind="attrs"
                                v-on="on"
                                class="mr-1"
                              >
                                <v-icon x-small>mdi-eye</v-icon>
                              </v-btn>
                            </template>
                            <span>View Details</span>
                          </v-tooltip>

                          <!-- Edit Button -->
                          <v-tooltip bottom v-if="!isDeletePage()">
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                x-small
                                color="primary"
                                :to="`/doctor-available-days/${item.id}/edit`"
                                v-bind="attrs"
                                v-on="on"
                                class="mr-1"
                              >
                                <v-icon x-small>mdi-pencil</v-icon>
                              </v-btn>
                            </template>
                            <span>Edit</span>
                          </v-tooltip>

                          <!-- Delete/Restore Button -->
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                x-small
                                :color="isDeletePage() ? 'success' : 'error'"
                                @click="
                                  isDeletePage()
                                    ? restoreItem(item)
                                    : deleteItem(item)
                                "
                                v-bind="attrs"
                                v-on="on"
                              >
                                <v-icon x-small>
                                  {{ isDeletePage() ? 'mdi-restore' : 'mdi-delete' }}
                                </v-icon>
                              </v-btn>
                            </template>
                            <span>{{ isDeletePage() ? 'Restore' : 'Delete' }}</span>
                          </v-tooltip>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <!-- Pagination -->
              <v-divider v-if="data && data.length > 0"></v-divider>
              <div
                v-if="data && data.length > 0"
                class="d-flex justify-center py-3"
              >
                <v-pagination
                  v-model="page"
                  :length="totalPages"
                  :total-visible="7"
                  color="primary"
                  @input="getData"
                  circle
                ></v-pagination>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Delete Confirmation Dialog -->
    <DeleteConfirmationDialog
      :dialog="deleteDialog"
      :item="selectedItem"
      @confirm="confirmDelete"
      @cancel="deleteDialog = false"
    />

    <!-- Restore Confirmation Dialog -->
    <v-dialog v-model="restoreDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">Restore Available Day</v-card-title>
        <v-card-text>
          Are you sure you want to restore this doctor available day?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="restoreDialog = false">
            Cancel
          </v-btn>
          <v-btn color="success" text @click="confirmRestore">
            Restore
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog.vue";

export default {
  name: "DoctorAvailableDaysIndex",
  components: {
    DeleteConfirmationDialog,
  },
  data() {
    return {
      // Loading and data states
      isLoading: false,
      data: [],
      count: 0,
      
      // Pagination
      page: 1,
      itemsPerPage: 10,
      selectedItemsPerPage: 10,
      
      // Filters
      filter: {
        search_keyword: null,
        user_id: null,
        day: null,
        is_available: null,
      },
      
      // Dropdown data
      users: [],
      days: [
        { text: "Monday", value: "Monday" },
        { text: "Tuesday", value: "Tuesday" },
        { text: "Wednesday", value: "Wednesday" },
        { text: "Thursday", value: "Thursday" },
        { text: "Friday", value: "Friday" },
        { text: "Saturday", value: "Saturday" },
        { text: "Sunday", value: "Sunday" },
      ],
      availabilityStatuses: [
        { text: "Available", value: 1 },
        { text: "Not Available", value: 0 },
      ],
      
      // Dialog states
      deleteDialog: false,
      restoreDialog: false,
      selectedItem: null,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.count / this.itemsPerPage);
    },
    hasActiveFilters() {
      return !!(
        this.filter.search_keyword ||
        this.filter.user_id ||
        this.filter.day ||
        this.filter.is_available !== null
      );
    },
  },
  mounted() {
    this.getData();
    this.getUsers();
  },
  methods: {
    // Data fetching
    async getData() {
      this.isLoading = true;
      try {
        const params = {
          page: this.page,
          rowsPerPage: this.itemsPerPage,
          show_deleted: this.isDeletePage(),
          ...this.filter,
        };

        // Remove null/undefined values
        Object.keys(params).forEach(key => {
          if (params[key] === null || params[key] === undefined || params[key] === '') {
            delete params[key];
          }
        });

        const response = await this.$http.get("/doctor_available_days", { params });
        
        this.data = response.data.data || [];
        this.count = response.data.count || 0;
      } catch (error) {
        console.error("Error fetching doctor available days:", error);
        this.$store.dispatch("errors/handleError", error);
      } finally {
        this.isLoading = false;
      }
    },

    async getUsers() {
      try {
        const response = await this.$http.get("/doctor_available_days/dropdown");
        this.users = (response.data.data || []).map(user => ({
          text: `${user.name} (${user.email})`,
          value: user.id,
        }));
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    },

    // Filter methods
    clearAllFilters() {
      this.filter = {
        search_keyword: null,
        user_id: null,
        day: null,
        is_available: null,
      };
      this.page = 1;
      this.getData();
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.search_keyword) count++;
      if (this.filter.user_id) count++;
      if (this.filter.day) count++;
      if (this.filter.is_available !== null) count++;
      return count;
    },

    // Pagination methods
    changeItemsPerPageToggle() {
      this.itemsPerPage = this.selectedItemsPerPage;
      this.page = 1;
      this.getData();
    },

    // Helper methods
    isDeletePage() {
      return this.$route.path.includes("/deleted");
    },

    getDayColor(day) {
      const colors = {
        Monday: "blue",
        Tuesday: "green",
        Wednesday: "orange",
        Thursday: "purple",
        Friday: "teal",
        Saturday: "red",
        Sunday: "indigo",
      };
      return colors[day] || "grey";
    },

    getDayType(day) {
      const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
      return weekdays.includes(day) ? "Weekday" : "Weekend";
    },

    getDayTypeColor(day) {
      const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
      return weekdays.includes(day) ? "primary" : "warning";
    },

    // Action methods
    deleteItem(item) {
      this.selectedItem = item;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      try {
        await this.$http.post(`/doctor_available_days/delete/${this.selectedItem.id}`);
        this.$store.dispatch("errors/showSuccess", "Available day deleted successfully");
        this.getData();
      } catch (error) {
        console.error("Error deleting available day:", error);
        this.$store.dispatch("errors/handleError", error);
      } finally {
        this.deleteDialog = false;
        this.selectedItem = null;
      }
    },

    restoreItem(item) {
      this.selectedItem = item;
      this.restoreDialog = true;
    },

    async confirmRestore() {
      try {
        await this.$http.post(`/doctor_available_days/restore/${this.selectedItem.id}`);
        this.$store.dispatch("errors/showSuccess", "Available day restored successfully");
        this.getData();
      } catch (error) {
        console.error("Error restoring available day:", error);
        this.$store.dispatch("errors/handleError", error);
      } finally {
        this.restoreDialog = false;
        this.selectedItem = null;
      }
    },

    // Export method
    async exportToExcel() {
      try {
        const params = {
          show_deleted: this.isDeletePage(),
          ...this.filter,
        };

        // Remove null/undefined values
        Object.keys(params).forEach(key => {
          if (params[key] === null || params[key] === undefined || params[key] === '') {
            delete params[key];
          }
        });

        const response = await this.$http.get("/doctor_available_days/excel", {
          params,
          responseType: 'blob',
        });

        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `doctor_available_days_${new Date().toISOString().slice(0,10)}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.$store.dispatch("errors/showSuccess", "Export completed successfully");
      } catch (error) {
        console.error("Error exporting data:", error);
        this.$store.dispatch("errors/handleError", error);
      }
    },
  },
};
</script>

<style scoped>
.v-chip {
  font-size: 0.75rem !important;
}

.v-simple-table th {
  font-size: 0.75rem !important;
  color: rgba(0, 0, 0, 0.87) !important;
}

.v-simple-table td {
  font-size: 0.875rem !important;
}

.v-pagination {
  justify-content: center;
}
</style>